/**
 * An item's preview copy made from a video file picked in its card (Νέο θέμα, the item's card) or dropped on its panel.
 * The browser reads the file first (`lookAt`). When it can decode it (MP4, MOV, WebM, MKV, MPEG-TS with H.264, HEVC,
 * VP9 or AV1, as the browser allows) and encode H.264 (or VP9) and AAC (or Opus), it makes the preview itself, to the
 * agent's measure (`PREVIEW`: 640×360 inside black bars, a keyframe a second, half the rate of a 50/60 Hz source),
 * and sends only that, a few MB. Anything else (MXF, MPEG-2, ProRes, DNxHD, a 4:2:2 or 10-bit H.264 the browser cannot
 * decode…) goes to the server in pieces, where FFmpeg makes the preview with the agent's commands and the file is then
 * deleted. A piece that does not arrive is sent again; the same file picked again for the same item goes on where it
 * stopped. The jobs belong to the page: they go on while the user moves around AirPost, not once the tab is closed
 * (the page asks before it is).
 */
import { useSyncExternalStore } from 'react';
import { ALL_FORMATS, BlobSource, BufferTarget, CanvasSink, Conversion, ConversionCanceledError, Input, Mp4OutputFormat, Output, getFirstEncodableAudioCodec, getFirstEncodableVideoCodec, type AudioCodec, type VideoCodec } from 'mediabunny';
import { PREVIEW, proxyFps, thumbAtMs, type IngestState } from '@airpost/shared';
import { ApiError, del, get, post, sendBytes } from './api.js';
import { rememberSendRate } from './send-time.js';

/** What the browser saw of a file, and who will make its preview. */
export interface FileLook {
  /** Made here, only the preview sent; made on the server, the file sent; or none (no picture in the file). */
  way: 'browser' | 'server' | 'none';
  durationMs: number | null;
  width: number | null;
  height: number | null;
  codec: string | null;
  fps: number | null;
  hasAudio: boolean;
  /** What the browser encodes the preview in (the browser's way). */
  encode: { video: VideoCodec; audio: AudioCodec | null } | null;
}

const unread: FileLook = { way: 'server', durationMs: null, width: null, height: null, codec: null, fps: null, hasAudio: false, encode: null };

/** Read what the file is, as far as the browser can, and whether it can make the preview itself. */
export async function lookAt(file: File): Promise<FileLook> {
  if (typeof VideoDecoder === 'undefined' || typeof VideoEncoder === 'undefined') return unread;
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
  try {
    const video = await input.getPrimaryVideoTrack();
    const audio = await input.getPrimaryAudioTrack();
    if (!video) return { ...unread, way: 'none', hasAudio: !!audio };
    const stats = await video.computePacketStats(60).catch(() => null);
    const seen: FileLook = {
      ...unread,
      durationMs: Math.round((await input.computeDuration()) * 1000),
      width: video.displayWidth,
      height: video.displayHeight,
      codec: video.codec,
      fps: stats?.averagePacketRate ? Math.round(stats.averagePacketRate * 1000) / 1000 : null,
      hasAudio: !!audio,
    };
    const [vOk, aOk, vEnc, aEnc] = await Promise.all([
      video.canDecode(),
      audio ? audio.canDecode() : true,
      getFirstEncodableVideoCodec(['avc', 'vp9'], { width: PREVIEW.width, height: PREVIEW.height }),
      audio ? getFirstEncodableAudioCodec(['aac', 'opus'], { numberOfChannels: 2, sampleRate: 48_000 }) : null,
    ]);
    if (!vOk || !aOk || !vEnc || (audio && !aEnc)) return seen;
    return { ...seen, way: 'browser', encode: { video: vEnc, audio: aEnc } };
  } catch {
    // a container the browser does not read (MXF, AVI…): the server reads it
    return unread;
  } finally {
    input.dispose();
  }
}

/** The browser could not make this preview after all: the server is asked to. */
class NeedsServer extends Error {
  constructor() {
    super('Ο browser δεν μπόρεσε να φτιάξει την προεπισκόπηση και ο server δεν έχει FFmpeg');
  }
}

/** The preview made in the browser (and its picture), from a file `lookAt` found it can make one of. */
async function makeInBrowser(file: File, look: FileLook, onProgress: (f: number) => void, signal: AbortSignal): Promise<{ mp4: Blob; jpeg: Blob | null }> {
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
  try {
    const target = new BufferTarget();
    const output = new Output({ format: new Mp4OutputFormat({ fastStart: 'in-memory' }), target });
    const conversion = await Conversion.init({
      input,
      output,
      tracks: 'primary',
      video: { width: PREVIEW.width, height: PREVIEW.height, fit: 'contain', frameRate: proxyFps(look.fps), codec: look.encode!.video, bitrate: PREVIEW.videoBps, keyFrameInterval: 1, forceTranscode: true },
      audio: look.encode!.audio ? { codec: look.encode!.audio, bitrate: PREVIEW.audioBps, numberOfChannels: 2, sampleRate: 48_000, forceTranscode: true } : { discard: true },
    });
    // the picture and (when the file has it) the sound must both be in it, or the server makes it
    const kinds = new Set(conversion.utilizedTracks.map((tr) => tr.type));
    if (!conversion.isValid || !kinds.has('video') || (look.hasAudio && !kinds.has('audio'))) throw new NeedsServer();
    const stop = () => void conversion.cancel();
    signal.addEventListener('abort', stop, { once: true });
    conversion.onProgress = (p) => onProgress(p);
    try {
      await conversion.execute();
    } finally {
      signal.removeEventListener('abort', stop);
    }
    if (!target.buffer) throw new NeedsServer();
    return { mp4: new Blob([target.buffer], { type: 'video/mp4' }), jpeg: await picture(file, look.durationMs) };
  } finally {
    input.dispose();
  }
}

/** The item's picture from the file: a quarter in (at most 10 s), 480×270 inside black bars, JPEG. */
async function picture(file: File, durationMs: number | null): Promise<Blob | null> {
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
  try {
    const track = await input.getPrimaryVideoTrack();
    if (!track) return null;
    const sink = new CanvasSink(track, { width: PREVIEW.thumbWidth, height: PREVIEW.thumbHeight, fit: 'contain' });
    const shot = await sink.getCanvas((await track.getFirstTimestamp()) + thumbAtMs(durationMs ?? 0) / 1000);
    if (!shot) return null;
    const c = document.createElement('canvas');
    c.width = PREVIEW.thumbWidth;
    c.height = PREVIEW.thumbHeight;
    const g = c.getContext('2d')!;
    g.fillStyle = '#000';
    g.fillRect(0, 0, c.width, c.height);
    g.drawImage(shot.canvas, 0, 0, c.width, c.height);
    return await new Promise<Blob | null>((res) => c.toBlob(res, 'image/jpeg', 0.82));
  } catch {
    return null;
  } finally {
    input.dispose();
  }
}

// ── the jobs ──

export type JobStage = 'making' | 'sending' | 'server' | 'done' | 'failed' | 'cancelled';
export interface PreviewJob {
  key: string;
  videoId: string;
  title: string;
  fileName: string;
  fileSize: number;
  way: 'browser' | 'server';
  stage: JobStage;
  /** How far the stage is, 0–1. */
  progress: number;
  /** What the server is doing with the file (the server's way). */
  serverStage: IngestState['stage'] | null;
  /** Bytes per second while the file is sent, and the seconds still to go. */
  rate: number | null;
  etaS: number | null;
  error: string | null;
}

let jobs: PreviewJob[] = [];
const listeners = new Set<() => void>();
const doneListeners = new Set<(videoId: string) => void>();
const controllers = new Map<string, AbortController>();
const emit = () => listeners.forEach((l) => l());
const patch = (key: string, p: Partial<PreviewJob>) => {
  jobs = jobs.map((j) => (j.key === key ? { ...j, ...p } : j));
  emit();
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** The jobs, for a component to draw. */
export const usePreviewJobs = (): PreviewJob[] => useSyncExternalStore(subscribe, () => jobs);
/** Heard once an item's preview has been kept. */
export function onPreviewDone(l: (videoId: string) => void): () => void {
  doneListeners.add(l);
  return () => doneListeners.delete(l);
}
export const isRunning = (j: PreviewJob): boolean => j.stage === 'making' || j.stage === 'sending' || j.stage === 'server';

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (jobs.some(isRunning)) e.preventDefault();
  });
}

const aborted = (e: unknown) => (e instanceof DOMException && e.name === 'AbortError') || e instanceof ConversionCanceledError;
const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((res, rej) => {
    if (signal.aborted) return rej(new DOMException('Aborted', 'AbortError'));
    const stop = () => {
      window.clearTimeout(h);
      rej(new DOMException('Aborted', 'AbortError'));
    };
    const h = window.setTimeout(() => {
      signal.removeEventListener('abort', stop);
      res();
    }, ms);
    signal.addEventListener('abort', stop, { once: true });
  });

/** Make an item's preview from a file (a job of the page); `serverCan` = the server has FFmpeg. */
export function startPreview(videoId: string, title: string, file: File, look: FileLook, serverCan: boolean): void {
  // a new file for the item replaces a job still running for it
  for (const j of jobs) if (j.videoId === videoId && isRunning(j)) cancelPreview(j.key);
  jobs = jobs.filter((j) => j.videoId !== videoId);
  const key = `${videoId}:${Date.now()}`;
  const ac = new AbortController();
  controllers.set(key, ac);
  const way = look.way === 'browser' ? 'browser' : 'server';
  jobs = [...jobs, { key, videoId, title, fileName: file.name, fileSize: file.size, way, stage: way === 'browser' ? 'making' : 'sending', progress: 0, serverStage: null, rate: null, etaS: null, error: null }];
  emit();
  void run(key, videoId, file, look, serverCan, ac.signal).finally(() => controllers.delete(key));
}

export function cancelPreview(key: string): void {
  controllers.get(key)?.abort();
}
export function dismissPreview(key: string): void {
  jobs = jobs.filter((j) => j.key !== key || isRunning(j));
  emit();
}

/** The fraction shown moves once a percent, the rest once a second (the page is not redrawn for every frame or chunk). */
function progressOf(key: string) {
  let shown = -1;
  let at = 0;
  return (f: number, more: Partial<PreviewJob> = {}) => {
    const pct = Math.floor(Math.min(1, Math.max(0, f)) * 100);
    const now = performance.now();
    if (pct === shown && (!Object.keys(more).length || now - at < 1000)) return;
    shown = pct;
    at = now;
    patch(key, { progress: pct / 100, ...more });
  };
}

async function run(key: string, videoId: string, file: File, look: FileLook, serverCan: boolean, signal: AbortSignal): Promise<void> {
  try {
    if (look.way === 'browser') {
      try {
        const made = await makeInBrowser(file, look, progressOf(key), signal);
        patch(key, { stage: 'sending', progress: 0 });
        const sent = progressOf(key);
        await sendBytes('PUT', `/api/archive/items/${videoId}/proxy`, made.mp4, { 'content-type': 'video/mp4', 'x-duration-ms': String(look.durationMs ?? 0), 'x-width': String(PREVIEW.width), 'x-height': String(PREVIEW.height), 'x-source-name': encodeURIComponent(file.name), 'x-source-size': String(file.size) }, { signal, onProgress: (n) => sent(n / made.mp4.size) });
        if (made.jpeg) await sendBytes('PUT', `/api/archive/items/${videoId}/thumb`, made.jpeg, { 'content-type': 'image/jpeg' }, { signal }).catch((e: unknown) => (aborted(e) ? Promise.reject(e) : undefined));
        finish(key, videoId);
        return;
      } catch (e) {
        // the browser could not make it after all (a frame it could not decode, say): the server makes it, when it can
        if (aborted(e) || e instanceof ApiError || !serverCan) throw e;
        patch(key, { way: 'server', stage: 'sending', progress: 0 });
      }
    }
    await viaServer(key, videoId, file, signal);
    finish(key, videoId);
  } catch (e) {
    if (aborted(e) || signal.aborted) patch(key, { stage: 'cancelled' });
    else patch(key, { stage: 'failed', error: e instanceof Error ? e.message : String(e) });
  }
}

function finish(key: string, videoId: string): void {
  patch(key, { stage: 'done', progress: 1, rate: null, etaS: null });
  doneListeners.forEach((l) => l(videoId));
}

/** The file to the server in pieces, then the server makes the preview; followed until it is kept (or failed). */
async function viaServer(key: string, videoId: string, file: File, signal: AbortSignal): Promise<void> {
  let up = (await post<{ upload: IngestState }>(`/api/archive/items/${videoId}/ingest`, { name: file.name, size: file.size })).upload;
  const id = up.id;
  // stopped here: the server stops too and deletes what it had
  signal.addEventListener('abort', () => void del(`/api/archive/ingest/${id}`).catch(() => undefined), { once: true });
  const shown = progressOf(key);
  const t0 = performance.now();
  const from = up.received;
  const show = (at: number) => {
    const s = (performance.now() - t0) / 1000;
    const rate = s > 2 ? (at - from) / s : null;
    shown(at / file.size, { rate, etaS: rate ? Math.round((file.size - at) / rate) : null });
  };
  let offset = up.received;
  show(offset);
  while (offset < file.size) {
    const at = offset;
    const piece = file.slice(at, Math.min(file.size, at + up.chunkBytes));
    for (let tries = 0; ; tries++) {
      try {
        up = (await sendBytes<{ upload: IngestState }>('PUT', `/api/archive/ingest/${id}`, piece, { 'content-type': 'application/octet-stream', 'x-offset': String(at) }, { signal, onProgress: (n) => show(at + n) })).upload;
        offset = up.received;
        break;
      } catch (e) {
        if (aborted(e)) throw e;
        // the server has other than what was thought: it says how much, and the file goes on from there
        const told = e instanceof ApiError && e.code === 'GAP' ? (e.payload as { upload?: IngestState } | null)?.upload : null;
        if (told) {
          offset = told.received;
          break;
        }
        const lasting = e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429;
        if (lasting || tries >= 6) throw e;
        await sleep(Math.min(30_000, 1000 * 2 ** tries), signal);
      }
    }
    show(offset);
  }
  // how fast this one went: the next file's card says how long it will take
  rememberSendRate(file.size - from, (performance.now() - t0) / 1000);
  patch(key, { stage: 'server', progress: 0, rate: null, etaS: null });
  up = (await post<{ upload: IngestState }>(`/api/archive/ingest/${id}/finish`)).upload;
  const made = progressOf(key);
  while (up.stage !== 'done' && up.stage !== 'failed') {
    made(up.progress, { serverStage: up.stage });
    await sleep(1500, signal);
    up = (await get<{ upload: IngestState }>(`/api/archive/ingest/${id}`)).upload;
  }
  if (up.stage === 'failed') throw new Error(up.error ?? 'Η προεπισκόπηση δεν έγινε');
}
